import React, { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import cn from 'classnames';
import { AxiosResponse } from 'axios';
import { useLiveQuery } from 'dexie-react-hooks';
import { useLocation, useParams } from 'react-router-dom';

import { db } from '../../../db';
import { Loader } from 'components/ui/Loader';
import { StabilityLevel } from 'components/ui/StabilityLevel';
import { useNetworkCheck } from 'contexts/NetworkContextProvider';
import { useLoadingWithErrorHandling } from 'hooks/useLoadingWithErrorHandling';
import { StabilityChart } from 'components/assessment-details';
import { getAssessmentResultsByPatientId, getMissingAssessmentsByPatientId } from 'services/api';
import {
  AssessmentResultsType,
  AssessmentResultType,
  changeDirection,
  MissingAssessmentType,
  MissingAssessmentsType,
} from 'types/assessment';
import { STABILITY_INDICATORS } from 'pages/AssessmentDetails';

export type DosesDataType = {
  currentMorphineDose: number;
  currentClonidineDose: number;
  currentClonidineFrequency: number;
};

const MAX_RESULTS = 5;

type ReviewAssessmentStepProps = {
  assessmentResult: AssessmentResultType;
};

export const ReviewAssessmentStep: React.FC<ReviewAssessmentStepProps> = ({ assessmentResult }) => {
  const [assessmentResults, setAssessmentResults] = useState<(AssessmentResultType | MissingAssessmentType)[]>([]);
  const [currentAssessmentIndex, setCurrentAssessmentIndex] = useState<number>(0);

  const location = useLocation();
  const { isOnline } = useNetworkCheck();

  const { id: patientId } = useParams();

  const assessmentResultFromDB = useLiveQuery(() => db.assessmentResults.where({ ref_id: patientId }).toArray());

  const mapDatabaseResultsToAssessmentResults = useCallback((): AssessmentResultType[] => {
    if (!assessmentResultFromDB) return [];

    return assessmentResultFromDB.map(result => {
      return {
        id: '0',
        version: result.version,
        hours_old: result.hours_old,
        created: result.created.toISOString(),
        overall_score: result.overall_score,
        assessment_result_symptom: result.assessment_result_symptom,
        assessment_result_drug: result.assessment_result_drug,
        comfortRecommendations: [],
      };
    });
  }, [assessmentResultFromDB]);

  let isCallAssessment: boolean = false;

  if (location.state) {
    ({ isCallAssessment } = location.state as { isCallAssessment: boolean });
  }

  const [isStartAssessment, setStartAssessment] = useState<boolean>(isCallAssessment || false);

  const { submitting, setSubmitting, showErrorModal, ErrorModalComponent } = useLoadingWithErrorHandling();

  const currentResult = useMemo(
    () => assessmentResults[currentAssessmentIndex],
    [assessmentResults, currentAssessmentIndex],
  );

  const priorResult = useMemo(() => {
    const filtered = assessmentResults.filter(ar => 'assessment_result_drug' in ar);
    return filtered[filtered.length - 1] as AssessmentResultType;
  }, [assessmentResults]);

  const fetchPatientAssessmentResults = useCallback(async () => {
    if (!patientId) return;

    setSubmitting(true);

    try {
      const [{ data: assessmentResultsData }, { data: missingAssessmentsData }]: [
        AxiosResponse<AssessmentResultsType>,
        AxiosResponse<MissingAssessmentsType>,
      ] = await Promise.all([getAssessmentResultsByPatientId(patientId), getMissingAssessmentsByPatientId(patientId)]);

      let array = assessmentResultsData.result;
      missingAssessmentsData.result.forEach(missingAssessment => {
        const index = array.findIndex(result => result.id === missingAssessment.assessment_result_id);
        array = [...array.slice(0, index - 1), missingAssessment, ...array.slice(index - 1)] as any;
      });
      const sortedResults = sortedByDate(array);
      const lastFewResults = sortedResults.slice(-MAX_RESULTS);
      lastFewResults.push(assessmentResult);
      setAssessmentResults(lastFewResults);
      setCurrentAssessmentIndex(lastFewResults.length - 1);
    } catch (error) {
      console.error(error);
      showErrorModal(error);
    } finally {
      setSubmitting(false);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, assessmentResult]);

  useEffect(() => {
    if (isStartAssessment && priorResult) {
      setStartAssessment(false);
    }
  }, [isStartAssessment, priorResult]);

  useEffect(() => {
    if (assessmentResultFromDB?.length) {
      setAssessmentResults(mapDatabaseResultsToAssessmentResults());
    }
  }, [assessmentResultFromDB, mapDatabaseResultsToAssessmentResults]);

  useEffect(() => {
    if (isOnline) {
      Promise.all([fetchPatientAssessmentResults()]);
    }
  }, [isOnline, fetchPatientAssessmentResults]);

  const sortedByDate = (data: AssessmentResultType[]) => {
    return data.sort((a, b) => {
      const dateA = dayjs(a.created);
      const dateB = dayjs(b.created);
      return dateA.isAfter(dateB) ? 1 : -1;
    });
  };

  const isSymptomScoreNotBelowThreshold = (symptomId: string, checkingScore: number): boolean => {
    if (!('assessment_result_symptom' in currentResult)) {
      return false;
    }
    const symptom = currentResult.assessment_result_symptom.find(s => s.symptom_id === symptomId);
    return !(!symptom || symptom.score <= checkingScore);
  };

  const isSymptomChecked = (symptomId: string): boolean => {
    if (!('symptom' in currentResult)) {
      return false;
    }
    return currentResult.symptom?.some(s => s === symptomId);
  };

  const determineDirection = useMemo(() => {
    if (assessmentResults.length < 2 || currentAssessmentIndex === 0) return changeDirection.noChange;

    const currentLevel = assessmentResults[currentAssessmentIndex].overall_score;
    const previousLevel = assessmentResults[currentAssessmentIndex - 1].overall_score;

    if (currentLevel > previousLevel) return changeDirection.decrease;
    if (currentLevel < previousLevel) return changeDirection.increase;

    return changeDirection.noChange;
  }, [assessmentResults, currentAssessmentIndex]);

  if (submitting || !assessmentResults.length)
    return (
      <div className="flex items-center h-[100vh]">
        <Loader />
      </div>
    );

  return (
    <div className="flex flex-col content-between ">
      <div className="pb-6">
        <div className="flex flex-col md:flex-row items-start md:justify-between">
          <h2 className={cn('text-gray-900 dark:text-white-400 text-lg md:text-[28px] font-semibold leading-[normal]')}>
            Review Stability Level
          </h2>
        </div>
      </div>
      <div className="flex flex-col content-between w-full max-w-[768px] mx-auto">
        <div className="flex flex-col gap-6">
          <div className="monitor-stability flex flex-col gap-4 bg-white-400 dark:bg-gray-900 shadow-section pt-6 px-6 pb-[22px] rounded-2xl">
            {!isOnline && (
              <>
                <h2 className="text-gray-900 dark:text-white-400 text-base font-semibold leading-7">Stability Level</h2>
                <StabilityLevel
                  level={currentResult.overall_score}
                  levelDirection={changeDirection.noChange}
                  isShortVersion
                />
                <h2 className={cn('mt-3 text-gray-900 dark:text-white-400 text-base font-semibold leading-7 pb-3')}>
                  Clinical Stability Indicators (CSI)
                </h2>
              </>
            )}
            {isOnline && (
              <>
                <div className="hidden md:block">
                  <StabilityChart
                    currentLevelIndex={currentAssessmentIndex}
                    stabilityLevels={assessmentResults.map(result => result.overall_score)}
                    levelDirection={determineDirection}
                    onSelectResult={(index: number) => setCurrentAssessmentIndex(index)}
                  />
                </div>
                <div className="block md:hidden">
                  <h2 className="text-gray-900 dark:text-white-400 text-base font-semibold leading-7">
                    Stability Level
                  </h2>
                  <StabilityLevel
                    level={currentResult.overall_score}
                    levelDirection={changeDirection.noChange}
                    // isShortVersion
                  />
                </div>
                <h2
                  className={cn(
                    'mt-3 md:-mt-6 text-gray-900 dark:text-white-400 text-base font-semibold leading-7 pb-3',
                  )}
                >
                  Clinical Stability Indicators (CSI)
                </h2>
              </>
            )}
            <div className="flex flex-wrap">
              {STABILITY_INDICATORS.map((indicator, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center gap-2.5 w-1/2 md:w-1/6 my-[11.5px] border-r-[1px] last:border-none border-white-300"
                >
                  <indicator.icon
                    className={
                      isSymptomScoreNotBelowThreshold(indicator.symptomId, indicator.checkingScore) ||
                      isSymptomChecked(indicator.symptomId)
                        ? indicator.unstableClass
                        : indicator.stableClass
                    }
                  />
                  <p className="text-gray-900 dark:text-white-400 text-center text-xs font-medium leading-[17px]">
                    {indicator.title}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {ErrorModalComponent}
      </div>
    </div>
  );
};
